import type { TabelaRef } from "./HubDpUi";

// O conteúdo de REFERÊNCIA dos cinco módulos do HUB DP — dado tipado, não banco.
//
// ---------------------------------------------------------------------------
// Por que constante tipada, e não o motor de conteúdo da 000073
// ---------------------------------------------------------------------------
// A doutrina da casa é que enunciado de formulário é DADO — as 15 perguntas do IR
// Proposta moram em `irp_perguntas` porque quatro delas diziam "no ano de 2025" e
// expiravam sozinhas em janeiro. E o IR Pro e o MEI Pro têm CMS de verdade,
// porque lá o conteúdo é vivo: 215 mil e 393 mil caracteres, com a equipe
// editando.
//
// Aqui é outra natureza, e o critério tem três perguntas, na ordem:
//
// **1) O texto expira sozinho?** O que expirava saiu daqui ANTES de qualquer
// outra decisão: as faixas de INSS e IRRF, o salário mínimo e as deduções foram
// para `dp_parametros`/`dp_faixas` (000108), e as tabelas da aba Referência são
// MONTADAS a partir do que o banco devolve — não há uma linha de faixa escrita
// neste arquivo. A versão do leiaute do eSocial saiu para parâmetro. O que sobrou
// aqui explica a CLT, a Lei 8.212/91 e o Decreto 6.042/2007: não tem temporada.
//
// **2) Quem mantém, e precisa editar sem deploy?** Ninguém pediu. O autor real é
// a equipe que já escreveu estes textos no app de origem, e o conteúdo foi
// preservado — o que mudou é onde vive e como é apresentado.
//
// **3) O volume pede CMS?** São 9 tabelas e 10 diagnósticos de rejeição. Montar
// editor, ordenação e bucket para isso seria construir uma tela que ninguém
// abriria.
//
// Se um dia a equipe quiser editar sem deploy, o caminho é pendurá-los no motor
// da 000073 com um card próprio — está anotado no runbook como pendência
// NOMEADA, não silenciosa. Enquanto ninguém pedir, **constante tipada é mais
// simples e não mente sobre quem mantém o texto**.

// ---------------------------------------------------------------------------
// Holerite
// ---------------------------------------------------------------------------

export const REFERENCIA_HOLERITE: TabelaRef[] = [
  {
    titulo: "Adicionais e a base de cada um",
    colunas: ["Adicional", "Percentual", "Base de cálculo", "Fundamento"],
    linhas: [
      ["Hora extra", "mínimo 50%", "hora normal (salário ÷ 220)", "CF art. 7º, XVI"],
      ["Adicional noturno (urbano)", "20%", "hora normal", "CLT art. 73"],
      ["Periculosidade", "30%", "salário base", "CLT art. 193, §1º"],
      ["Insalubridade", "10%, 20% ou 40%", "salário mínimo", "CLT art. 192"],
      ["Vale-transporte (desconto)", "até 6%", "salário base", "Lei 7.418/85, art. 4º"],
      ["FGTS (depósito)", "8%", "remuneração do mês", "Lei 8.036/90, art. 15"],
    ],
    nota:
      "Periculosidade e insalubridade não se acumulam: o empregado opta pelo mais vantajoso " +
      "(CLT art. 193, §2º). A CCT da categoria pode fixar percentual ou base diferente.",
  },
  {
    titulo: "Erros que aparecem com mais frequência",
    colunas: ["O erro", "O que acontece", "O certo"],
    linhas: [
      [
        "Somar o FGTS aos descontos",
        "o líquido sai menor do que o devido",
        "o FGTS é depósito do empregador, fora do recibo de descontos",
      ],
      [
        "Calcular insalubridade sobre o salário do empregado",
        "adicional inflado em salários altos",
        "a base é o salário mínimo (Súmula Vinculante 4 do STF)",
      ],
      [
        "Abater o INSS na base do Modelo 2",
        "imposto menor que o devido",
        "o desconto simplificado substitui todas as deduções, o INSS incluído",
      ],
      [
        "Descontar o vale-transporte dos proventos totais",
        "desconto maior em mês com hora extra",
        "o limite de 6% é do salário base",
      ],
      [
        "Aplicar a alíquota do INSS sem a parcela a deduzir",
        "contribuição muito acima da real",
        "a tabela é progressiva: alíquota da faixa × base − parcela a deduzir",
      ],
    ],
  },
];

// ---------------------------------------------------------------------------
// Férias e 13º
// ---------------------------------------------------------------------------

export const REFERENCIA_FERIAS: TabelaRef[] = [
  {
    titulo: "Férias — prazos e regras",
    colunas: ["Regra", "O que diz", "Fundamento"],
    linhas: [
      ["Direito", "30 dias após 12 meses de trabalho", "CLT art. 130"],
      ["Fracionamento", "até 3 períodos; um deles com 14 dias ou mais", "CLT art. 134, §1º"],
      ["Aviso ao empregado", "no mínimo 30 dias de antecedência", "CLT art. 135"],
      ["Venda de dias (abono)", "até 1/3 do período, ou seja 10 dias", "CLT art. 143"],
      ["Pagamento", "até 2 dias antes do início do gozo", "CLT art. 145"],
      ["Concessão em dobro", "férias não concedidas em 12 meses", "CLT art. 137"],
      ["Terço constitucional", "1/3 sobre a remuneração das férias", "CF art. 7º, XVII"],
    ],
  },
  {
    titulo: "13º salário — parcelas e prazos",
    colunas: ["Evento", "Base", "Prazo"],
    linhas: [
      ["1ª parcela", "metade da remuneração, sem descontos", "até 30 de novembro"],
      ["2ª parcela", "13º integral menos INSS, IRRF e o adiantamento", "até 20 de dezembro"],
      ["DARF do IRRF (0561)", "IRRF do 13º, tributação exclusiva", "até o 2º dia útil de janeiro"],
      ["FGTS do 13º", "8% sobre o 13º bruto", "dia 20 do mês seguinte ao pagamento"],
    ],
    nota:
      "A 1ª parcela pode ser paga junto das férias, se o empregado pedir por escrito até 31 de " +
      "janeiro (Lei 4.749/65, art. 2º, §2º).",
  },
  {
    titulo: "O que sofre e o que não sofre INSS e IRRF",
    colunas: ["Verba", "INSS", "IRRF", "Por quê"],
    linhas: [
      ["Férias gozadas", "sim", "sim", "é remuneração"],
      ["Terço constitucional", "sim", "sim", "acompanha as férias"],
      ["Abono pecuniário (dias vendidos)", "não", "não", "verba indenizatória"],
      ["Terço sobre o abono", "não", "não", "acompanha o abono"],
      ["13º salário", "sim", "sim (exclusivo na fonte)", "não entra no ajuste anual"],
      ["1ª parcela do 13º", "não", "não", "os descontos saem todos na 2ª"],
    ],
    nota:
      "A não incidência sobre o abono está no art. 28, §9º, 'e', 6, da Lei 8.212/91 e na Súmula " +
      "125 do STJ. Tributar o abono é o erro clássico do módulo.",
  },
];

// ---------------------------------------------------------------------------
// Encargos e FGTS
// ---------------------------------------------------------------------------

export const REFERENCIA_ENCARGOS: TabelaRef[] = [
  {
    titulo: "Encargos patronais — composição",
    colunas: ["Encargo", "Base", "Alíquota", "Fundamento"],
    linhas: [
      ["INSS patronal", "folha de pagamento", "20%", "Lei 8.212/91, art. 22, I"],
      ["RAT", "folha de pagamento", "1%, 2% ou 3% pelo grau de risco", "Lei 8.212/91, art. 22, II"],
      ["FAP", "multiplica o RAT", "0,5 a 2,0", "Decreto 6.042/2007"],
      ["Terceiros (Sistema S)", "folha de pagamento", "varia pelo FPAS, em torno de 5,8%", "Lei 8.212/91, art. 3º"],
      ["FGTS", "remuneração", "8%", "Lei 8.036/90, art. 15"],
    ],
    nota:
      "O custo típico fica entre 36% e 40% da folha no regime de 20%. Terceiros variam por " +
      "atividade: consulte o código FPAS da empresa antes de usar o valor padrão.",
  },
  {
    titulo: "CPRB — a desoneração da folha",
    colunas: ["O que substitui", "O que NÃO substitui"],
    linhas: [
      ["o INSS patronal de 20% sobre a folha", "o RAT × FAP, que continua sobre a folha"],
      ["", "os terceiros, que continuam sobre a folha"],
      ["", "o FGTS, que continua sobre a remuneração"],
      ["", "a contribuição descontada do empregado"],
    ],
    nota:
      "A opção é anual e irretratável (Lei 12.546/2011). Nem sempre é vantajosa: em empresa com " +
      "receita alta e folha pequena, a CPRB custa mais. A calculadora mostra os dois lados.",
  },
  {
    titulo: "FGTS na rescisão, por motivo",
    colunas: ["Motivo", "Multa", "Saque", "Seguro-desemprego"],
    linhas: [
      ["Dispensa sem justa causa", "40%", "integral", "sim, se elegível"],
      ["Culpa recíproca", "20%", "integral", "não"],
      ["Dispensa por justa causa", "não há", "não saca", "não"],
      ["Pedido de demissão", "não há", "não saca", "não"],
      ["Acordo (art. 484-A)", "20%", "80% do saldo", "não"],
    ],
    nota:
      "A culpa recíproca só é reconhecida pela Justiça do Trabalho (Súmula 14 do TST). O acordo do " +
      "art. 484-A é a única hipótese com saque parcial.",
  },
];

// ---------------------------------------------------------------------------
// eSocial
// ---------------------------------------------------------------------------

export interface Rejeicao {
  codigo: string;
  titulo: string;
  evento: string;
  causa: string;
  solucao: string;
}

/**
 * As dez rejeições mais frequentes, com causa e solução.
 *
 * Conteúdo do app de origem, preservado. São diagnósticos de comportamento do
 * Ambiente Nacional, não valores — não expiram por ano.
 */
export const REJEICOES: Rejeicao[] = [
  {
    codigo: "301",
    titulo: "Evento de tabela não encontrado",
    evento: "S-1200, S-2200",
    causa:
      "A rubrica, a lotação ou o estabelecimento referenciados não existem no Ambiente Nacional, " +
      "ou a vigência informada está fora do período em que a tabela vale.",
    solucao:
      "Transmita primeiro o S-1010 (rubricas), o S-1020 (lotações) ou o S-1005 (estabelecimentos), " +
      "e confira a data de início de validade. Tabela antes de quem a usa — é o motivo de a ordem " +
      "do cronograma existir.",
  },
  {
    codigo: "302",
    titulo: "Vínculo não encontrado",
    evento: "S-1200, S-2299, S-2206",
    causa:
      "O CPF ou a matrícula não correspondem a um vínculo ativo. Quase sempre é o S-2200 que não " +
      "foi processado, ou a matrícula digitada diferente da que foi enviada na admissão.",
    solucao:
      "Consulte o vínculo no portal e confirme a matrícula EXATA do S-2200. Matrícula é chave: " +
      "'001' e '1' são vínculos diferentes para o eSocial.",
  },
  {
    codigo: "305",
    titulo: "Duplicidade de evento",
    evento: "todos",
    causa:
      "O mesmo evento já foi recebido com a mesma chave. Costuma acontecer quando o retorno demora " +
      "e o operador reenvia achando que falhou.",
    solucao:
      "Consulte o recibo antes de reenviar. Para corrigir um evento já aceito, use retificação " +
      "(indRetif = 2) com o número do recibo original — não um novo envio.",
  },
  {
    codigo: "312",
    titulo: "Período de apuração encerrado",
    evento: "S-1200, S-1210",
    causa: "O S-1299 daquela competência já foi transmitido, e o período está fechado.",
    solucao:
      "Reabra a competência com o S-1298 (reabertura), envie a correção e feche de novo com o " +
      "S-1299. Não há como incluir remuneração em período fechado.",
  },
  {
    codigo: "402",
    titulo: "CPF inválido ou não encontrado na base do CNIS",
    evento: "S-2200, S-2190",
    causa:
      "O CPF não existe, está com situação irregular na Receita, ou o nome e a data de nascimento " +
      "não batem com o cadastro.",
    solucao:
      "Confira os três dados juntos — CPF, nome completo e data de nascimento — no portal da " +
      "Receita. O eSocial valida a combinação, não só o dígito.",
  },
  {
    codigo: "404",
    titulo: "Data de admissão anterior à do vínculo anterior",
    evento: "S-2200",
    causa:
      "Existe outro vínculo ativo do mesmo CPF na mesma empresa, cujo desligamento não foi enviado.",
    solucao:
      "Transmita o S-2299 do vínculo anterior antes da nova admissão. Se o vínculo antigo foi " +
      "encerrado sem evento, o desligamento precisa ser enviado em atraso primeiro.",
  },
  {
    codigo: "407",
    titulo: "Admissão fora do prazo",
    evento: "S-2200",
    causa:
      "O evento chegou depois do início das atividades. O prazo é até o dia imediatamente anterior.",
    solucao:
      "Não há como corrigir o atraso — o evento é aceito e o registro fica em atraso, sujeito à " +
      "multa do art. 47 da CLT. É o item crítico do roteiro de admissão por essa razão.",
  },
  {
    codigo: "501",
    titulo: "Rubrica com incidência incompatível",
    evento: "S-1010, S-1200",
    causa:
      "A natureza da rubrica não combina com o código de incidência informado — por exemplo, verba " +
      "indenizatória marcada com incidência de INSS.",
    solucao:
      "Reveja a tabela de rubricas contra a tabela 3 do leiaute. O abono pecuniário de férias é o " +
      "caso que mais aparece: ele não tem incidência de INSS nem de IRRF.",
  },
  {
    codigo: "630",
    titulo: "Certificado digital inválido ou vencido",
    evento: "todos",
    causa:
      "O certificado usado na assinatura expirou, foi revogado, ou o procurador não tem procuração " +
      "eletrônica vigente para o CNPJ.",
    solucao:
      "Renove o certificado e confira a procuração no e-CAC. Certificado A1 vence em um ano e o " +
      "vencimento não avisa antes.",
  },
  {
    codigo: "701",
    titulo: "Fechamento com divergência de totais",
    evento: "S-1299",
    causa:
      "Os totais apurados no Ambiente Nacional não batem com o que a folha calculou — em geral por " +
      "rubrica não enviada, ou por remuneração de trabalhador que ficou fora do S-1200.",
    solucao:
      "Compare o relatório de totais do portal com o da folha, competência por competência. A " +
      "diferença aponta a rubrica ou o trabalhador que faltou.",
  },
];

export const REFERENCIA_ESOCIAL: TabelaRef[] = [
  {
    titulo: "A ordem de transmissão, e por que ela importa",
    colunas: ["Etapa", "Eventos", "Quando"],
    linhas: [
      ["1. Tabelas", "S-1000, S-1005, S-1010, S-1020", "antes de qualquer evento que as referencie"],
      ["2. Não-periódicos", "S-2200, S-2206, S-2230, S-2299", "no prazo de cada fato"],
      ["3. Periódicos", "S-1200, S-1210, S-1280", "até o dia 15 do mês seguinte"],
      ["4. Fechamento", "S-1299", "depois de todos os periódicos"],
      ["5. Apuração", "DCTFWeb", "gerada a partir do fechamento, vence dia 20"],
      ["6. FGTS", "FGTS Digital", "vence dia 20"],
    ],
    nota:
      "Fora dessa ordem, o Ambiente Nacional rejeita com o código 301 (tabela não encontrada) ou " +
      "312 (período encerrado) — são duas das dez rejeições mais comuns.",
  },
];

// ---------------------------------------------------------------------------
// Admissão
// ---------------------------------------------------------------------------

export const REFERENCIA_ADMISSAO: TabelaRef[] = [
  {
    titulo: "CLT, estágio e aprendiz — o que muda",
    colunas: ["Aspecto", "CLT", "Estágio", "Aprendiz"],
    linhas: [
      ["Vínculo de emprego", "sim", "não", "sim, especial"],
      ["Idade mínima", "16 anos (14 como aprendiz)", "matrícula ativa", "14 a 24 anos"],
      ["Jornada máxima", "8h diárias / 44h semanais", "6h diárias / 30h semanais", "6h (8h com médio concluído)"],
      ["FGTS", "8%", "não há", "2%"],
      ["INSS", "sim", "não", "sim"],
      ["13º salário", "sim", "não", "sim"],
      ["Férias + 1/3", "sim", "recesso de 30 dias", "sim"],
      ["Evento do eSocial", "S-2200", "S-2200 (categoria 901)", "S-2200 (categoria 103)"],
    ],
    nota:
      "Estágio é regido pela Lei 11.788/2008; aprendizagem, pelo art. 428 da CLT e pelo Decreto " +
      "9.579/2018. Estágio sem os requisitos da lei configura vínculo de emprego.",
  },
  {
    titulo: "Documentos do empregado na admissão",
    colunas: ["Documento", "Para quê"],
    linhas: [
      ["CPF", "identificação em todos os eventos do eSocial"],
      ["Documento de identidade", "qualificação no contrato"],
      ["PIS / NIS", "vínculo previdenciário (pode ser gerado no S-2200)"],
      ["Comprovante de residência", "vale-transporte e cadastro"],
      ["Comprovante de escolaridade", "campo grauInstr do S-2200"],
      ["Certidão de casamento", "dependentes e salário-família"],
      ["Documentos dos filhos", "salário-família e dedução de IRRF"],
      ["Certidão de nascimento dos filhos até 14 anos", "salário-família"],
      ["Caderneta de vacinação em dia", "manutenção do salário-família"],
      ["Dados bancários", "pagamento"],
      ["ASO admissional", "NR-7 — antes do início das atividades"],
    ],
    nota:
      "A CTPS é digital e não se pede a carteira física. Guarde só o necessário: dado pessoal " +
      "coletado sem finalidade é exposição sem motivo (Lei 13.709/2018).",
  },
];
